import React, { useEffect, useRef, useState } from 'react';
import { useGameStore } from '@/state/gameStore';
import {
  EngineStatus,
  getEngineStatus,
  initialisePhaserEngine,
} from '@/services/phaserEngine';
import { PerformanceMonitor } from './PerformanceMonitor';
import { DialogueModal } from './DialogueModal';
import { InventoryOverlay } from './InventoryOverlay';
import { Minimap } from './Minimap';
import { GitHubBranchOverlay } from './GitHubBranchOverlay';
import { modeStateSyncService } from '@/services/modeStateSyncService';
import { combatTransitionService } from '@/services/combatTransitionService';
import type { RailwayNPCData } from '@/services/railwayWorldStateService';

interface GraphicalModeProps {
  onExit?: () => void;
}

/**
 * Temporary placeholder for the upcoming Phaser-powered renderer.
 * Renders a debug panel so we can wire up routing / toggles before the engine lands.
 */
const GraphicalMode: React.FC<GraphicalModeProps> = ({ onExit }) => {
  // Safely access Zustand store with fallbacks
  // Wrap in try-catch to handle potential store initialization issues
  const userProfile = useGameStore((state) => state.userProfile);
  const currentStorySegment = useGameStore((state) => state.currentStorySegment);
  const currentPlayerState = useGameStore((state) => state.currentPlayerState);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [engineStatus, setEngineStatus] = useState<EngineStatus>(getEngineStatus());
  const [initialisationError, setInitialisationError] = useState<string | null>(null);
  const destroyHandleRef = useRef<(() => Promise<void>) | null>(null);
  
  // Dialogue modal state
  const [selectedNPC, setSelectedNPC] = useState<RailwayNPCData | null>(null);
  const [isDialogueOpen, setIsDialogueOpen] = useState(false);
  
  // Inventory overlay state
  const [isInventoryOpen, setIsInventoryOpen] = useState(false);
  
  // Minimap state
  const [isMinimapVisible, setIsMinimapVisible] = useState(true);
  const [playerPosition, setPlayerPosition] = useState({ x: 400, y: 300 });
  const [npcs, setNpcs] = useState<RailwayNPCData[]>([]);
  
  // GitHub branch overlay state
  const [isGitHubOverlayVisible, setIsGitHubOverlayVisible] = useState(false);
  
  // Combat transition state
  const [isTransitioningToCombat, setIsTransitioningToCombat] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let retryCount = 0;
    const maxRetries = 10;
    const retryDelay = 100;

    const checkContainerReady = (): boolean => {
      if (!containerRef.current) {
        return false;
      }
      
      // Check if container is attached to DOM
      if (!containerRef.current.parentElement) {
        return false;
      }
      
      // Check if container has dimensions
      const rect = containerRef.current.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        return false;
      }
      
      return true;
    };

    const initialise = async () => {
      if (cancelled) return;

      // Sync state from text mode before initializing graphical mode
      modeStateSyncService.saveStateBeforeSwitch();
      const graphicalState = modeStateSyncService.syncFromText(
        modeStateSyncService.getTextState()
      );

      // Check container readiness
      if (!checkContainerReady()) {
        retryCount++;
        if (retryCount < maxRetries) {
          console.warn(`[GraphicalMode] Container not ready (attempt ${retryCount}/${maxRetries}), retrying...`);
          setTimeout(initialise, retryDelay * retryCount); // Exponential backoff
          return;
        } else {
          console.error('[GraphicalMode] Container not ready after max retries');
          if (!cancelled) {
            setInitialisationError('Container element not ready. Please refresh the page.');
            setEngineStatus('error');
          }
          return;
        }
      }

      try {
        console.log('[GraphicalMode] Initializing Phaser engine...');
        setInitialisationError(null);
        setEngineStatus('initialising');

        if (!containerRef.current) {
          throw new Error('Container ref is null');
        }

        const handle = await initialisePhaserEngine({
          container: containerRef.current,
          debug: false,
        });

        console.log('[GraphicalMode] Phaser engine initialized successfully, status:', handle.status);

        // Store destroy handle in ref (not state) to persist across renders
        destroyHandleRef.current = handle.destroy;

        if (!cancelled) {
          setEngineStatus(handle.status);
          console.log('[GraphicalMode] Engine ready, canvas should be visible');
        } else {
          // Component was cancelled during initialization, destroy immediately
          await handle.destroy();
        }
      } catch (error) {
        console.error('[GraphicalMode] Engine initialisation failed:', error);
        if (!cancelled) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          setInitialisationError(`Renderer initialisation failed: ${errorMessage}. Check console for details.`);
          setEngineStatus('error');
        }
      }
    };

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(initialise, 100);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);

      // Sync state back to text mode before unmounting
      if (destroyHandleRef.current) {
        modeStateSyncService.restoreStateAfterSwitch();
      }

      // Only destroy if we're truly unmounting, not just re-rendering
      if (destroyHandleRef.current) {
        console.log('[GraphicalMode] Component unmounting, destroying Phaser...');
        destroyHandleRef.current().catch((error) => {
          console.warn('[GraphicalMode] Failed to destroy Phaser instance:', error);
        });
        destroyHandleRef.current = null;
      }
    };
  }, []); // Empty deps - only run once

  // Listen for NPC click events from Phaser
  useEffect(() => {
    const handleNPCClick = (event: CustomEvent<{ npc: RailwayNPCData }>) => {
      setSelectedNPC(event.detail.npc);
      setIsDialogueOpen(true);
    };

    window.addEventListener('npc-clicked', handleNPCClick as EventListener);

    return () => {
      window.removeEventListener('npc-clicked', handleNPCClick as EventListener);
    };
  }, []);

  // Listen for inventory toggle (I key)
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'i' || event.key === 'I') {
        setIsInventoryOpen((prev) => !prev);
      }
      if (event.key === 'm' || event.key === 'M') {
        setIsMinimapVisible((prev) => !prev);
      }
      if (event.key === 'g' || event.key === 'G') {
        setIsGitHubOverlayVisible((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

        // Listen for player position updates from Phaser
        useEffect(() => {
          const handlePlayerMove = (event: CustomEvent<{ x: number; y: number }>) => {
            setPlayerPosition({ x: event.detail.x, y: event.detail.y });
            
            // Sync position to store periodically
            const store = useGameStore.getState();
            if (store.currentPlayerState) {
              // Position is stored in graphical mode, sync on exit
              // For now, we'll sync on unmount
            }
          };

          window.addEventListener('player-moved', handlePlayerMove as EventListener);

          return () => {
            window.removeEventListener('player-moved', handlePlayerMove as EventListener);
          };
        }, []);

        // Listen for NPC data updates from Phaser
        useEffect(() => {
          const handleNPCData = (event: CustomEvent<{ npcs: RailwayNPCData[] }>) => {
            setNpcs(event.detail.npcs);
          };

          window.addEventListener('npc-data', handleNPCData as EventListener);

          return () => {
            window.removeEventListener('npc-data', handleNPCData as EventListener);
          };
        }, []);

        // Listen for Phaser errors
        useEffect(() => {
          const handlePhaserError = (event: CustomEvent<{ error: string; context: string; stack?: string }>) => {
            console.error('[GraphicalMode] Phaser error received:', event.detail);
            setInitialisationError(`Phaser error in ${event.detail.context}: ${event.detail.error}`);
            setEngineStatus('error');
          };

          window.addEventListener('phaser-error', handlePhaserError as EventListener);

          return () => {
            window.removeEventListener('phaser-error', handlePhaserError as EventListener);
          };
        }, []);

  // Listen for combat navigation events
  useEffect(() => {
    const handleNavigateToCombat = async () => {
      console.log('[GraphicalMode] Combat transition triggered');
      
      // Save graphical state before transitioning
      const graphicalState = modeStateSyncService.getGraphicalState();
      combatTransitionService.saveBeforeCombat(graphicalState, playerPosition);
      
      // Set transitioning state
      setIsTransitioningToCombat(true);
      
      // Dispatch event to App.tsx to navigate to combat
      // The App.tsx handler will take care of the actual navigation
      window.dispatchEvent(new CustomEvent('navigate-to-combat'));
    };

    window.addEventListener('navigate-to-combat', handleNavigateToCombat);

    return () => {
      window.removeEventListener('navigate-to-combat', handleNavigateToCombat);
    };
  }, [playerPosition]);

  // Restore state after returning from combat (if applicable)
  useEffect(() => {
    // Check if we're returning from combat
    if (combatTransitionService.hasPendingTransition() && !isTransitioningToCombat) {
      const restoredState = combatTransitionService.restoreAfterCombat();
      if (restoredState) {
        console.log('[GraphicalMode] Restored state after combat');
        // State is restored, Phaser scene will pick it up on next render
      }
    }
  }, [isTransitioningToCombat]);

  // Handle exit callback with state sync
  const handleExit = () => {
    // Sync state before exiting
    modeStateSyncService.restoreStateAfterSwitch();
    
    if (onExit) {
      onExit();
    }
  };

  return (
    <section
      className="min-h-screen bg-slate-950 text-slate-200 flex flex-col items-center justify-center p-8"
      aria-label="Graphical mode under construction"
    >
      {/* Performance Monitor - visible in dev mode */}
      {import.meta.env.DEV && <PerformanceMonitor visible={true} position="top-right" />}
      
      <div className="max-w-xl w-full bg-slate-900 border border-cyan-500/40 rounded-xl p-6 shadow-lg text-center">
        <h1 className="text-2xl font-semibold text-cyan-300 mb-4">
          Dreamscape Visual Mode (Work in Progress)
        </h1>
        <p className="text-sm text-slate-400 leading-relaxed">
          The graphical overworld is being brought online. This placeholder keeps routing, shared
          state wiring, and accessibility in place while the Phaser renderer is integrated. Expect
          the initial release to include tile-based navigation, sprite pooling, and seamless syncing
          with the narrative log.
        </p>
        <div className="mt-6 text-left text-xs text-slate-400 space-y-2">
          <p>
            <span className="font-semibold text-slate-200">Active Dreamer:</span>{' '}
            {userProfile?.name ?? 'Unknown'}
          </p>
          <p>
            <span className="font-semibold text-slate-200">Current Location:</span>{' '}
            {currentPlayerState?.location ?? 'Uncharted Sector'}
          </p>
          <p>
            <span className="font-semibold text-slate-200">Story Preview:</span>{' '}
            {currentStorySegment?.narrative?.slice(0, 160) ?? 'No narrative loaded yet.'}
          </p>
          <p className="pt-2">
            Asset and telemetry guidelines live in{' '}
            <code className="text-cyan-300">docs/assets/STYLE_GUIDE.md</code> and{' '}
            <code className="text-cyan-300">docs/GRAPHICS_TELEMETRY.md</code>.
          </p>
          <p>
            <span className="font-semibold text-slate-200">Renderer Status:</span>{' '}
            {engineStatus}
          </p>
                 {initialisationError && (
                   <div className="mt-4 p-4 bg-red-900/50 border border-red-500 rounded-lg">
                     <p className="text-red-400 font-semibold mb-2">Error:</p>
                     <p className="text-red-300 text-sm">{initialisationError}</p>
                     <div className="mt-3 flex gap-2">
                       <button
                         type="button"
                         onClick={() => {
                           setInitialisationError(null);
                           setEngineStatus('idle');
                           // Trigger reinitialization by reloading
                           window.location.reload();
                         }}
                         className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm"
                       >
                         Retry
                       </button>
                       {onExit && (
                         <button
                           type="button"
                           onClick={handleExit}
                           className="px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-lg transition-colors text-sm"
                         >
                           Return to Text Mode
                         </button>
                       )}
                     </div>
                   </div>
                 )}
          <p className="pt-2 text-xs text-slate-400">
            <span className="font-semibold text-slate-200">Controls:</span> Arrow keys or click to move | I: Inventory | M: Minimap | G: GitHub Branches | Click NPCs to interact
          </p>
        </div>
        <div
          id="phaser-game-container"
          ref={containerRef}
          className="mt-6 w-full h-[420px] md:h-[520px] bg-slate-900 border border-slate-700/60 rounded-xl overflow-hidden shadow-inner"
          style={{ maxWidth: '100%' }}
        />
        <div className="mt-4 flex gap-4 justify-center">
          {onExit && (
            <button
              type="button"
              onClick={handleExit}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
            >
              Return to Text Mode
            </button>
          )}
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('navigate-to-combat'))}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            Enter Combat
          </button>
          <button
            type="button"
            onClick={() => setIsGitHubOverlayVisible(true)}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
          >
            View GitHub Branches
          </button>
        </div>
      </div>

      {/* Dialogue Modal */}
      {selectedNPC && (
        <DialogueModal
          npc={selectedNPC}
          isOpen={isDialogueOpen}
          onClose={() => {
            setIsDialogueOpen(false);
            setSelectedNPC(null);
          }}
          onChoice={(choice) => {
            console.log('[GraphicalMode] Dialogue choice:', choice);
            // TODO: Update game state/karma based on choice
          }}
        />
      )}

      {/* Inventory Overlay */}
      <InventoryOverlay
        isOpen={isInventoryOpen}
        onClose={() => setIsInventoryOpen(false)}
      />

      {/* Minimap */}
      <Minimap
        isVisible={isMinimapVisible}
        playerPosition={playerPosition}
        npcs={npcs}
        mapWidth={1600}
        mapHeight={1200}
      />

      {/* GitHub Branch Overlay */}
      <GitHubBranchOverlay
        isVisible={isGitHubOverlayVisible}
        onClose={() => setIsGitHubOverlayVisible(false)}
      />
    </section>
  );
};

export default GraphicalMode;
